import { ApplicationUserResponse, DefaultRoles, DeleteUserRolesRequest } from "@api/apiClient";
import { useDeleteUserRoles, useUsers } from "@api/users";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import { Box, Snackbar, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridDeleteIcon, GridRowId } from "@mui/x-data-grid-premium";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

// Admin roles to display in this grid
const ADMIN_ROLES = [DefaultRoles.Admin, DefaultRoles.IctSystemAdmin, DefaultRoles.ProjectSetupAdmin];

// Row type for flattened user-role data
interface AdminUserRow {
  id: string;
  email: string;
  role: string;
  userId: string;
}

const AdminsGrid = observer(() => {
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const [openDeleteToast, setOpenDeleteToast] = useState(false);
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  // Fetch all users without role filter
  const adminUsers = useUsers();
  const deleteUserClaims = useDeleteUserRoles();
  const confirm = useConfirm();

  useEffect(() => {
    // Flatten users so each user-role combination is a separate row
    const flattenedRows: AdminUserRow[] = [];

    adminUsers.data?.forEach((user: ApplicationUserResponse) => {
      // Get only admin roles for this user
      const adminRolesForUser = user.roles?.filter((role) => ADMIN_ROLES.includes(role as DefaultRoles)) || [];

      // Create a row for each admin role
      adminRolesForUser.forEach((role) => {
        flattenedRows.push({
          id: `${user.userId}-${role}`,
          email: user.email || "",
          role: role,
          userId: user.userId || "",
        });
      });
    });

    setRows(flattenedRows);
  }, [adminUsers.data]);

  const handleDeleteClick = async (id: GridRowId) => {
    const { confirmed } = await confirm({
      title: i18n.t("admin-tab.confirm-dialog.header"),
      description: i18n.t("admin-tab.confirm-dialog.message"),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });

    if (confirmed) {
      const deletingRow = rows.find((row) => row.id === id);
      if (deletingRow?.email && deletingRow?.role) {
        const deleteUserRolesRequest = new DeleteUserRolesRequest({
          email: deletingRow.email,
          role: deletingRow.role,
        } as any);

        await deleteUserClaims.mutateAsync(deleteUserRolesRequest);
        setOpenDeleteToast(true);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: "email",
      headerName: i18n.t("admin-tab.grid.header-email"),
      editable: false,
      flex: 1,
    },
    {
      field: "role",
      headerName: i18n.t("admin-tab.grid.header-role"),
      editable: false,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("admin-tab.grid.header-action"),
      resizable: false,
      width: 80,
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            key={`delete-${id}`}
            icon={
              <Tooltip title={i18n.t("admin-tab.grid.delete-icon-tooltip")}>
                <GridDeleteIcon />
              </Tooltip>
            }
            label={i18n.t("admin-tab.grid.delete-icon-title")}
            onClick={() => handleDeleteClick(id)}
          />,
        ];
      },
    },
  ];

  return (
    <>
      <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
        <DataGrid
          loading={adminUsers.isFetching || adminUsers.isPending}
          columns={columns}
          rows={rows}
          disableColumnMenu
          initialState={{
            sorting: {
              sortModel: [{ field: "email", sort: "asc" }],
            },
          }}
          sx={{ height: `${(height || 400) - 16}px` }}
        />
      </Box>
      <Snackbar
        open={openDeleteToast}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={i18n.t("admin-tab.grid.delete-success-message")}
        onClose={() => setOpenDeleteToast(false)}
      />
    </>
  );
});

export default AdminsGrid;
